import Functions from '@client/style/Functions';
import { Breakpoints } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import React, { useRef } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import HTML from '@client/core/utils/HTML';
import ChevronLeft from '@client/assets/svgs/chevron-left.svg';

const Container = styled.div<{ $bg?: string; $fg?: string }>`
	position: relative;
	padding-left: var(--gridMargin);
	padding-right: var(--gridMargin);
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;
	padding-bottom: 40px;
	padding-top: 40px;
	background-color: ${props => props.$bg};
	color: ${props => props.$fg};

	&:has(+ .BreakerModule) {
		border-bottom: var(--breaker-total) solid transparent;
	}
`;

const InsideContainer = styled.div`
	max-width: 740px;
	position: relative;
	margin: auto;
`;

const TitleContainer = styled.h2`
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	${TextStyle.size('p2')}
	text-align: center;
	padding-bottom: 10px;
	margin: 0px;
`;

const SubtitleContainer = styled.div`
	${TextStyle.family('title')}
	${TextStyle.size('large')}
	padding-bottom: 40px;
	text-align: center;
`;

const AccordianContainer = styled.div``;

const AccordianElement = styled.div`
	border: 2px solid currentColor;
	margin-bottom: 10px;
	padding: 0px 20px;
	:last-of-type {
		margin-bottom: 0px;
	}
	${Functions.breakpoint(Breakpoints.mobile)} {
		margin-bottom: 20px;
		padding: 0px 26px;

		:last-of-type {
			margin-bottom: 0px;
		}
	}
`;

const AccordianElementTitle = styled.div`
	padding-top: 17px;
	padding-bottom: 17px;
	${TextStyle.size('p6')}
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	align-items: start;
	gap: 10px;

	${Functions.breakpoint(Breakpoints.mobile)} {
		${TextStyle.size('p4')}
		line-height: 1.1;
		padding-bottom: 26px;
		padding-top: 26px;
	}
`;

const FoldIcon = styled.button`
	display: flex;
	align-items: center;
	color: currentColor;
`;

const AccordianElementContent = styled.div`
	${TextStyle.size('small')}
	height: 0px;
	overflow: hidden;
	${TextStyle.family('title')}

	a {
		${TextStyle.family('title')}
		${TextStyle.weight('bold')}
		text-decoration: underline;
	}
	p {
		padding-top: 10px;
		padding-bottom: 17px;
		${Functions.breakpoint(Breakpoints.mobile)} {
			padding-top: 0px;

			padding-bottom: 26px;
		}
	}
	ul {
		margin-bottom: 17px;
		${Functions.breakpoint(Breakpoints.mobile)} {
			padding-top: 0px;

			margin-bottom: 26px;
		}
	}
	${Functions.breakpoint(Breakpoints.mobile)} {
		${TextStyle.size('large')}
		line-height: 1.3;
	}
`;

interface AccordianProps {
	data: {
		title: string;
		subtitle: string;
		accordianElements: any;
		backgroundColorLink: any;
		moduleId: string;
	};
}

export default function Accordian(props: AccordianProps) {
	const AccordianTitleRefs = useRef(props.data.accordianElements.map(() => useRef(undefined)));
	const AccordianContentRefs = useRef(props.data.accordianElements.map(() => useRef(undefined)));
	const AccordianFoldRefs = useRef(props.data.accordianElements.map(() => useRef(undefined)));

	const toggleInfo = (index: number) => {
		const content = AccordianContentRefs.current[index].current;
		const fold = AccordianFoldRefs.current[index].current;
		if (content.style.height !== '0px' && window.getComputedStyle(content).height !== '0px') {
			gsap.to(content, { duration: 0.2, height: '0px', ease: 'power2.out' });
			gsap.to(fold, { duration: 0.2, rotation: 0, ease: 'power2.out' });
		} else {
			gsap.to(content, { duration: 0.2, height: 'auto', ease: 'power2.out' });
			gsap.to(fold, { duration: 0.2, rotation: -90, ease: 'power2.out' });
		}
	};

	return (
		<Container
			className='colorWrapperAnimatedModule'
			$bg={props.data.backgroundColorLink?.color}
			$fg={props.data.backgroundColorLink?.contrastColor}
			id={'module_' + props.data.moduleId}
		>
			<InsideContainer>
				{props.data.title && <TitleContainer>{props.data.title}</TitleContainer>}
				{props.data.subtitle && <SubtitleContainer>{props.data.subtitle}</SubtitleContainer>}
				<AccordianContainer>
					{props.data.accordianElements.map((element: any, index: number) => (
						<AccordianElement key={'accordian-' + index}>
							<AccordianElementTitle
								ref={AccordianTitleRefs.current[index]}
								onClick={() => {
									toggleInfo(index);
								}}
							>
								{element.title}
								<FoldIcon ref={AccordianFoldRefs.current[index]}>
									<ChevronLeft />
								</FoldIcon>
							</AccordianElementTitle>
							<AccordianElementContent
								dangerouslySetInnerHTML={{ __html: HTML.clean(element.information) }}
								ref={AccordianContentRefs.current[index]}
							></AccordianElementContent>
						</AccordianElement>
					))}
				</AccordianContainer>
			</InsideContainer>
		</Container>
	);
}
